import { useEffect, useRef, useState } from "react";
import { SUPPRESSION_REASON_LABELS, type SuppressionListRow, type SuppressionReason } from "@feedback-hub/contract/browser";

import { ApiError, getMailSuppressions, removeMailSuppression } from "../../api.js";
import { formatDate } from "../../format.js";
import type { SortDir, SortValue } from "../../sort.js";
import { SortableTh } from "./SortableTh.js";
import { useSort } from "./useSort.js";

type Key = "email" | "reason" | "sourceAppId" | "createdAt";

const DIRS: Record<Key, SortDir> = {
  email: "asc",
  reason: "asc",
  sourceAppId: "asc",
  createdAt: "desc",
};

function reasonLabel(reason: SuppressionReason): string {
  return SUPPRESSION_REASON_LABELS[reason];
}

function value(s: SuppressionListRow, key: Key): SortValue {
  switch (key) {
    case "email":
      return s.email;
    case "reason":
      return reasonLabel(s.reason);
    case "sourceAppId":
      return s.sourceAppId;
    case "createdAt":
      return s.createdAt;
  }
}

export function SuppressionsTab() {
  const [suppressions, setSuppressions] = useState<SuppressionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<SuppressionListRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const removingRef = useRef(false);
  const { sorted, sort, onSort } = useSort<SuppressionListRow, Key>(
    suppressions,
    value,
    { key: "createdAt", dir: "desc" },
    DIRS,
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getMailSuppressions();
        if (!cancelled) setSuppressions(res.suppressions);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load mail stats.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !removingRef.current) {
        setPending(null);
        setDialogError(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending]);

  function closeDialog() {
    if (removingRef.current) return;
    setPending(null);
    setDialogError(null);
  }

  function confirmRemove() {
    if (!pending || removingRef.current) return;
    const target = pending;
    removingRef.current = true;
    setRemoving(true);
    setDialogError(null);
    void (async () => {
      try {
        await removeMailSuppression(target.email);
        setSuppressions((rows) => rows.filter((row) => row.email !== target.email));
        setNotice(`Removed ${target.email} from suppression.`);
        setPending(null);
      } catch (err) {
        setDialogError(err instanceof ApiError ? err.message : "Could not remove that suppression.");
      } finally {
        removingRef.current = false;
        setRemoving(false);
      }
    })();
  }

  if (loading) return <p className="muted">Loading mail stats…</p>;
  if (error) return <p className="error">{error}</p>;

  const th = (label: string, k: Key) => (
    <SortableTh label={label} sortKey={k} activeKey={sort.key} dir={sort.dir} onSort={onSort} />
  );

  return (
    <div className="panel section">
      <h3>Global suppression list</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Addresses here are blocked for every app in the AWS account (SES account-level suppression).
        Removing one clears it from SES and from FeedbackHub; app-local lists are not changed. Spam
        complaints can only be removed with a script.
      </p>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {sorted.length === 0 ? (
        <p className="muted">Nothing suppressed.</p>
      ) : (
        <div className="table-wrap">
          <table className="queue">
            <thead>
              <tr>
                {th("Address", "email")}
                {th("Reason", "reason")}
                {th("Origin app", "sourceAppId")}
                {th("Since", "createdAt")}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <tr key={s.email} style={{ cursor: "default" }}>
                  <td className="mono">{s.email}</td>
                  <td>{reasonLabel(s.reason)}</td>
                  <td className="mono">{s.sourceAppId ?? "—"}</td>
                  <td>{s.createdAt ? formatDate(s.createdAt) : "—"}</td>
                  <td>
                    {s.reason === "complaint" ? (
                      <span className="muted">Script only</span>
                    ) : (
                      <button
                        type="button"
                        className="btn secondary"
                        style={{ minWidth: "6.5rem" }}
                        onClick={() => {
                          setDialogError(null);
                          setPending(s);
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pending && (
        <div className="confirm-backdrop" onClick={closeDialog}>
          <div
            className="confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-suppression-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="remove-suppression-title">Remove suppression</h3>
            <p>
              Remove {pending.email} from suppression? Every app will be able to email it again.
              App-local lists (for example Pleased to Announce's) are not changed.
            </p>
            {dialogError && (
              <p className="error" role="alert">
                {dialogError}
              </p>
            )}
            <div className="confirm-actions">
              <button type="button" className="btn secondary" disabled={removing} onClick={closeDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                style={{ minWidth: "6.5rem" }}
                disabled={removing}
                onClick={confirmRemove}
              >
                {removing && <span className="btn-spinner" aria-hidden="true" />}
                {removing ? "Removing" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
